import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Input,
  InputField,
  Pressable,
  Spinner,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useReferenceSearch } from './useReferenceSearch';
import ReferenceFieldBox from './ReferenceFieldBox';
import ReferencePill from './ReferencePill';
import type { ReferenceOption, ReferenceSelectFieldProps } from './types';

const shortId = (id: string): string => (id.length > 8 ? `${id.slice(0, 8)}…` : id);

const preventMouseDownFocusSteal = (event: { preventDefault: () => void }) => {
  event.preventDefault();
};

const labelForValue = (value: ReferenceOption, unknownLabel: string): string => {
  if (value.title) return value.title;
  return unknownLabel;
};

/**
 * ReferenceSelectField
 * ----
 * Single-value server search. A choice replaces the search with a pill inside
 * the box. The name is not editable. The X on the pill removes it.
 */
const ReferenceSelectField: React.FC<ReferenceSelectFieldProps> = ({
  value,
  onChange,
  objectName,
  fixedFilter,
  searchField = 'title',
  limit = 8,
  allowClear = false,
  testID = 'reference-select-field',
  searchPlaceholderKey = 'admin.common.references.searchPlaceholder',
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const {
    options,
    isLoading,
    error,
    retry,
  } = useReferenceSearch({
    objectName,
    query,
    fixedFilter,
    searchField,
    limit,
  });

  const hasSelection = !!value?.id;

  const selectedLabel = useMemo(() => {
    if (!value?.id) return '';
    return labelForValue(
      value,
      t('admin.common.references.unknownLabel', { id: shortId(value.id) }),
    );
  }, [t, value]);

  const showResults = !hasSelection && query.trim().length > 0;

  const pickOption = (option: ReferenceOption) => {
    onChange({ id: option.id, title: option.title ?? undefined });
    setQuery('');
    setIsFocused(false);
  };

  const clearSelection = () => {
    onChange(null);
    setQuery('');
    setIsFocused(false);
  };

  return (
    <VStack space="sm" testID={testID}>
      <ReferenceFieldBox focused={isFocused && !hasSelection}>
        {hasSelection ? (
          <ReferencePill
            testID="reference-pill"
            label={selectedLabel}
            onRemove={allowClear ? clearSelection : undefined}
            removeTestID="reference-clear"
            removeLabel={t('admin.common.references.remove')}
          />
        ) : (
          <Input flex={1} size="sm" borderWidth={0} bg="transparent">
            <InputField
              testID="reference-search"
              value={query}
              onChangeText={setQuery}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                setIsFocused(false);
                setQuery('');
              }}
              placeholder={t(searchPlaceholderKey)}
            />
          </Input>
        )}
      </ReferenceFieldBox>

      {!!error && !hasSelection && (
        <Pressable
          testID="reference-retry"
          onPress={retry}
          // @ts-expect-error - onMouseDown is a web-only prop
          onMouseDown={preventMouseDownFocusSteal}
          px="$3"
          py="$2"
          bg="$statusErrorSubtle"
          borderRadius="$md"
        >
          <Text size="sm" color="$statusError">
            {t('admin.common.references.retry')}
          </Text>
        </Pressable>
      )}

      {showResults && !error ? (
        isLoading ? (
          <Spinner size="small" testID="reference-search-loading" />
        ) : (
          <VStack
            borderWidth={1}
            borderColor="$borderLight"
            borderRadius="$md"
            overflow="hidden"
          >
            {options.slice(0, limit).map((option, index) => (
              <Pressable
                key={option.id}
                testID={`reference-pick-${option.id}`}
                onPress={() => pickOption(option)}
                // @ts-expect-error - onMouseDown is a web-only prop
                onMouseDown={preventMouseDownFocusSteal}
                px="$3"
                py="$2"
                borderBottomWidth={index < Math.min(options.length, limit) - 1 ? 1 : 0}
                borderBottomColor="$borderLight"
                $hover={{ bg: '$bgHover' }}
              >
                <Text size="sm" color="$textBody" numberOfLines={1}>
                  {option.title ?? t('admin.common.references.unknownLabel', {
                    id: shortId(option.id),
                  })}
                </Text>
              </Pressable>
            ))}
            {options.length === 0 && !isLoading ? (
              <Box px="$3" py="$2">
                <Text size="sm" color="$textSubtle">
                  {t('admin.common.references.noResults')}
                </Text>
              </Box>
            ) : null}
          </VStack>
        )
      ) : null}
    </VStack>
  );
};

export default ReferenceSelectField;
